import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { supabase } from "../lib/supabase";
import {
  canWriteAccountFavorites,
  parseDashboardFavorites,
  resolveSyncedList,
  type DashboardFavorite,
} from "../lib/toolFavorites";
import {
  Plus,
  X,
  Star,
  Briefcase,
  GraduationCap,
  Sparkles,
  type LucideIcon,
  // Workmate icons
  FileText,
  ListChecks,
  Calculator,
  Bot,
  Receipt,
  FileCheck,
  FileSignature,
  Mail,
  PenLine,
  PackageSearch,
  PanelsTopLeft,
  Images,
  Code,
  CalendarDays,
  Building2,
  Inbox,
  ClipboardList,
  FilePenLine,
  ShieldCheck,
  // Classmate icons
  Users,
  LayoutGrid,
  CalendarClock,
  Heart,
  Vote,
  Zap,
  CheckCircle,
  Timer,
  Presentation,
  Shuffle,
  QrCode,
  Cloud,
  Mic,
  ScanSearch,
  MessageCircle,
} from "lucide-react";

/* ─── Tool type ─── */
type QuickTool = {
  id: string;
  service: "workmate" | "classmate";
  name: string;
  shortName?: string;
  to?: string;
  href?: string;
  icon: LucideIcon;
};

/* ─── All available tools from Workmate ─── */
const WORKMATE_TOOLS: QuickTool[] = [
  { id: "teacher-jobs", service: "workmate", name: "교원 채용 지도", shortName: "채용지도", to: "/workmate/teacher-jobs", icon: Briefcase },
  { id: "poster-studio", service: "workmate", name: "포스터 만들기", shortName: "포스터", to: "/workmate/poster-studio", icon: FileText },
  { id: "school-documents", service: "workmate", name: "학교 문서 모아받기", shortName: "학교문서", to: "/workmate/school-documents", icon: FileText },
  { id: "file-converter", service: "workmate", name: "파일 변환", shortName: "파일변환", to: "/workmate/file-converter", icon: FileText },
  { id: "file-inbox", service: "workmate", name: "AI 파일 수합", shortName: "파일수합", to: "/workmate/file-inbox", icon: Inbox },
  { id: "exam-supervision", service: "workmate", name: "시험 감독시간표", shortName: "감독시간표", to: "/workmate/exam-supervision", icon: CalendarClock },
  { id: "student-record", service: "workmate", name: "생활기록부 문구 생성", shortName: "생기부", to: "/workmate/student-record", icon: FileText },
  { id: "evaluation-plan", service: "workmate", name: "평가 계획 작성", shortName: "평가계획", to: "/workmate/evaluation-plan", icon: ListChecks },
  { id: "exam-score", service: "workmate", name: "시험 문항 배점 생성기", shortName: "배점생성", to: "/workmate/exam-score", icon: Calculator },
  { id: "essay-grading", service: "workmate", name: "논술형 평가 채점", shortName: "논술채점", to: "/workmate/essay-grading", icon: FileCheck },
  { id: "data-lookup", service: "workmate", name: "개별 데이터 안심 조회", shortName: "안심조회", to: "/workmate/data-lookup", icon: ShieldCheck },
  { id: "consent-form", service: "workmate", name: "뚝딱 서류 수합기", shortName: "서류수합", to: "/workmate/consent-form", icon: Mail },
  { id: "collect", service: "workmate", name: "자료 수합", shortName: "자료수합", to: "/workmate/collect/dashboard", icon: Inbox },
  { id: "registry", service: "workmate", name: "등록부 서명 수합기", shortName: "등록부", to: "/workmate/registry", icon: FileSignature },
  { id: "document-sign", service: "workmate", name: "문서 서명 받기", shortName: "문서서명", to: "/workmate/document-sign", icon: PenLine },
  { id: "receipts", service: "workmate", name: "영수증 자동정리", shortName: "영수증", to: "/workmate/receipts", icon: Receipt },
  { id: "training-certs", service: "workmate", name: "연수 이수증 수합", shortName: "이수증", to: "/workmate/training-certs", icon: GraduationCap },
  { id: "hwpx-fill", service: "workmate", name: "AI HWPX 문서 자동생성", shortName: "HWPX생성", to: "/workmate/hwpx-fill", icon: FilePenLine },
  { id: "timetable", service: "workmate", name: "우리학교 시간표", shortName: "시간표", to: "/workmate/timetable", icon: CalendarDays },
  { id: "re-class", service: "workmate", name: "학급 편성", shortName: "학급편성", to: "/workmate/re-class", icon: PanelsTopLeft },
  { id: "lost-found", service: "workmate", name: "분실물 관리", shortName: "분실물", to: "/workmate/lost-found", icon: PackageSearch },
  { id: "equipment-loan", service: "workmate", name: "물품 대여 관리", shortName: "물품대여", to: "/workmate/equipment-loan", icon: ClipboardList },
  { id: "office-builder", service: "workmate", name: "온라인 교무실 만들기", shortName: "교무실", to: "/workmate/office-builder", icon: Building2 },
  { id: "school-conversation", service: "workmate", name: "익명 학교 대화", shortName: "학교대화", to: "/workmate/school-conversation", icon: MessageCircle },
  { id: "custom-gpt", service: "workmate", name: "맞춤 GPT", shortName: "맞춤GPT", to: "/workmate/custom-gpt", icon: Bot },
  { id: "latex-to-hwp", service: "workmate", name: "사진·한글 수식 편집", shortName: "수식변환", to: "/workmate/latex-to-hwp", icon: Code },
];

/* ─── All available tools from Classmate ─── */
const CLASSMATE_TOOLS: QuickTool[] = [
  { id: "record-studio", service: "classmate", name: "생기부 스튜디오", shortName: "생기부스튜디오", to: "/classmate/record-studio", icon: FilePenLine },
  { id: "peer-quiz", service: "classmate", name: "학생 문제 배틀", shortName: "문제배틀", to: "/classmate/peer-quiz", icon: Zap },
  { id: "group-match", service: "classmate", name: "모둠 매칭", shortName: "모둠매칭", to: "/classmate/group-match", icon: Users },
  { id: "wrong-answer-note", service: "classmate", name: "오답노트 생성기", shortName: "오답노트", to: "/classmate/wrong-answer-note", icon: FileText },
  { id: "classrooms", service: "classmate", name: "학급 관리", shortName: "학급관리", to: "/classmate/classrooms", icon: Users },
  { id: "arrangements", service: "classmate", name: "자리 배치", shortName: "자리배치", to: "/classmate/arrangements", icon: LayoutGrid },
  { id: "attendance-management", service: "classmate", name: "출석 관리", shortName: "출석관리", to: "/classmate/attendance-management", icon: CheckCircle },
  { id: "sociometry", service: "classmate", name: "마음관계 분석", shortName: "마음분석", to: "/classmate/sociometry", icon: Heart },
  { id: "votes", service: "classmate", name: "익명 투표", shortName: "익명투표", to: "/classmate/votes", icon: Vote },
  { id: "onepick", service: "classmate", name: "원픽 투표", shortName: "원픽투표", to: "/classmate/onepick", icon: ListChecks },
  { id: "progress", service: "classmate", name: "수업 진도", shortName: "수업진도", to: "/classmate/progress", icon: CalendarClock },
  { id: "activities", service: "classmate", name: "활동 기록", shortName: "활동기록", to: "/classmate/activities", icon: ClipboardList },
  { id: "albums", service: "classmate", name: "우리 반 사진함", shortName: "사진함", to: "/classmate/albums", icon: Images },
  { id: "walls", service: "classmate", name: "우리 반 담벼락", shortName: "담벼락", to: "/classmate/walls", icon: PanelsTopLeft },
  { id: "wordcloud", service: "classmate", name: "실시간 워드 클라우드", shortName: "워드클라우드", to: "/classmate/wordcloud", icon: Cloud },
  { id: "reading-assessment", service: "classmate", name: "스피치 발음 평가", shortName: "발음평가", to: "/classmate/reading-assessment", icon: Mic },
  { id: "essay-grading", service: "classmate", name: "논술형 평가", shortName: "논술평가", to: "/classmate/essay-grading", icon: FileCheck },
  { id: "quiz-battle", service: "classmate", name: "퀴즈 스튜디오", shortName: "퀴즈배틀", to: "/classmate/quiz-battle", icon: Zap },
  { id: "secret16", service: "classmate", name: "시크릿 16", shortName: "시크릿16", to: "/classmate/secret16", icon: ScanSearch },
  { id: "timer", service: "classmate", name: "수업 타이머", shortName: "타이머", to: "/classmate/timer", icon: Timer },
  { id: "board", service: "classmate", name: "전자칠판", shortName: "전자칠판", to: "/classmate/board", icon: Presentation },
  { id: "random-pick", service: "classmate", name: "랜덤 뽑기", shortName: "랜덤뽑기", to: "/classmate/random-pick", icon: Shuffle },
  { id: "qr", service: "classmate", name: "QR 코드 생성", shortName: "QR생성", to: "/classmate/qr", icon: QrCode },
];

const ALL_QUICK_TOOLS = [...WORKMATE_TOOLS, ...CLASSMATE_TOOLS];

const CLASSMATE_TOOL_ID_ALIASES: Record<string, string> = {
  games: "card-flip-games",
};

function resolveToolId(service: string, toolId: string): string {
  if (service === "classmate" && CLASSMATE_TOOL_ID_ALIASES[toolId]) {
    return CLASSMATE_TOOL_ID_ALIASES[toolId];
  }
  return toolId;
}

const MAX_SLOTS = 8;
const STORAGE_KEY = "dashboard-quick-favorites";

/* ─── Persistence helpers ─── */
type FavoriteEntry = DashboardFavorite;

function accountStorageKey(userId: string) {
  return `${STORAGE_KEY}:${userId}`;
}

function readStoredFavorites(key: string): FavoriteEntry[] | null {
  try {
    const raw = localStorage.getItem(key);
    if (raw == null) return null;
    return parseDashboardFavorites(JSON.parse(raw));
  } catch {
    return null;
  }
}

function saveToStorage(key: string, entries: FavoriteEntry[]) {
  try { localStorage.setItem(key, JSON.stringify(entries)); } catch { /* storage may be unavailable */ }
}

/* ─── Main Component ─── */
export function QuickFavorites() {
  const { user, isAuthenticated, loading } = useAuth();
  const userId = user ? String(user.id) : null;
  const [favorites, setFavorites] = useState<FavoriteEntry[]>([]);
  const [isLoaded, setIsLoaded] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [pickerTab, setPickerTab] = useState<"workmate" | "classmate">("workmate");
  const [isEditing, setIsEditing] = useState(false);
  const [removeAnimId, setRemoveAnimId] = useState<string | null>(null);
  const pickerRef = useRef<HTMLDivElement>(null);
  const lastSyncedSerializedRef = useRef<string | null>(null);
  const loadGenerationRef = useRef(0);
  const settledGenerationRef = useRef(0);
  const inflightRef = useRef<string | null>(null);
  const favoritesRef = useRef<FavoriteEntry[]>([]);

  useEffect(() => {
    if (!isAuthenticated) { setPickerOpen(false); return; }
    if (!pickerOpen) return;
    const previousFocus = document.activeElement as HTMLElement | null;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    pickerRef.current?.querySelector<HTMLButtonElement>("button")?.focus();
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") { event.preventDefault(); setPickerOpen(false); }
      if (event.key !== "Tab") return;
      const controls = pickerRef.current?.querySelectorAll<HTMLElement>('button:not(:disabled), input:not(:disabled), a[href]');
      if (!controls?.length) return;
      const first = controls[0], last = controls[controls.length - 1];
      if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
      else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
    };
    document.addEventListener("keydown", onKey);
    return () => { document.removeEventListener("keydown", onKey); document.body.style.overflow = previousOverflow; previousFocus?.focus(); };
  }, [pickerOpen, isAuthenticated]);

  // Load favorites from the account. Local cache is only a migration source
  // until this generation's cloud read settles, so an empty first paint cannot
  // overwrite the account.
  useEffect(() => {
    if (loading || !isAuthenticated || !userId || !supabase) {
      settledGenerationRef.current = 0;
      setIsLoaded(false);
      return;
    }

    const generation = ++loadGenerationRef.current;
    settledGenerationRef.current = 0;
    setIsLoaded(false);
    const accountUserId = userId;
    let cancelled = false;

    async function load() {
      const legacy = readStoredFavorites(STORAGE_KEY);
      const account = readStoredFavorites(accountStorageKey(accountUserId));
      const { data, error } = await supabase!.auth.getUser();
      if (cancelled || generation !== loadGenerationRef.current) return;

      if (error) {
        const fallback = (account ?? legacy ?? []).slice(0, MAX_SLOTS);
        favoritesRef.current = fallback;
        lastSyncedSerializedRef.current = JSON.stringify(fallback);
        settledGenerationRef.current = generation;
        setFavorites(fallback);
        setIsLoaded(true);
        return;
      }

      const metadata = data.user?.user_metadata as Record<string, unknown> | undefined;
      const cloud = metadata && Object.prototype.hasOwnProperty.call(metadata, "dashboard_favorites")
        ? parseDashboardFavorites(metadata.dashboard_favorites)
        : null;
      const resolved = resolveSyncedList({
        cloud,
        legacy,
        account,
        key: (entry) => `${entry.service}:${entry.toolId}`,
      });
      const items = resolved.items.slice(0, MAX_SLOTS);
      favoritesRef.current = items;
      lastSyncedSerializedRef.current = resolved.writeCloud
        ? JSON.stringify(cloud ?? [])
        : JSON.stringify(items);
      if (!resolved.writeCloud) {
        saveToStorage(accountStorageKey(accountUserId), items);
        if (resolved.clearLegacy) localStorage.removeItem(STORAGE_KEY);
      }
      settledGenerationRef.current = generation;
      setFavorites(items);
      setIsLoaded(true);
    }

    load().catch((error) => {
      console.error(error);
      if (cancelled || generation !== loadGenerationRef.current) return;
      lastSyncedSerializedRef.current = JSON.stringify(favoritesRef.current);
      settledGenerationRef.current = generation;
      setIsLoaded(true);
    });

    return () => {
      cancelled = true;
    };
  }, [loading, isAuthenticated, userId]);

  // Persist changes to the account only after the cloud read has settled.
  useEffect(() => {
    if (!isLoaded || !isAuthenticated || !userId || !supabase) return;
    if (
      !canWriteAccountFavorites({
        authLoading: loading,
        loadGeneration: loadGenerationRef.current,
        settledGeneration: settledGenerationRef.current,
      })
    ) {
      return;
    }

    const accountUserId = userId;
    const entries = favoritesRef.current;
    const serialized = JSON.stringify(entries);
    if (lastSyncedSerializedRef.current === serialized || inflightRef.current === serialized) return;

    const generation = loadGenerationRef.current;
    inflightRef.current = serialized;
    void supabase.auth
      .updateUser({ data: { dashboard_favorites: entries } })
      .then(({ error }) => {
        if (generation !== loadGenerationRef.current) return;
        if (error) throw error;
        lastSyncedSerializedRef.current = serialized;
        inflightRef.current = null;
        saveToStorage(accountStorageKey(accountUserId), entries);
        localStorage.removeItem(STORAGE_KEY);
      })
      .catch((error) => {
        console.error(error);
        if (inflightRef.current === serialized) inflightRef.current = null;
      });
  }, [favorites, isLoaded, isAuthenticated, loading, userId]);

  const isFavorited = useCallback(
    (service: string, toolId: string) =>
      favorites.some((f) => f.service === service && f.toolId === toolId),
    [favorites]
  );

  function addFavorite(service: string, toolId: string) {
    if (!isLoaded) return;
    if (favorites.length >= MAX_SLOTS) return;
    if (isFavorited(service, toolId)) return;
    setFavorites((prev) => {
      const next = [...prev, { service, toolId }];
      favoritesRef.current = next;
      return next;
    });
  }

  function removeFavorite(service: string, toolId: string) {
    if (!isLoaded) return;
    const key = `${service}:${toolId}`;
    setRemoveAnimId(key);
    setTimeout(() => {
      setFavorites((prev) => {
        const next = prev.filter((f) => !(f.service === service && f.toolId === toolId));
        favoritesRef.current = next;
        return next;
      });
      setRemoveAnimId(null);
    }, 250);
  }

  function toggleFavorite(service: string, toolId: string) {
    if (isFavorited(service, toolId)) {
      removeFavorite(service, toolId);
    } else {
      addFavorite(service, toolId);
    }
  }

  const resolvedTools = useMemo(() =>
    favorites
      .map((f) => {
        const toolId = resolveToolId(f.service, f.toolId);
        return ALL_QUICK_TOOLS.find((t) => t.service === f.service && t.id === toolId);
      })
      .filter(Boolean) as QuickTool[]
  , [favorites]);

  const emptySlots = MAX_SLOTS - resolvedTools.length;

  if (!isAuthenticated) return null;

  return (
    <section className="fe-quick-favorites py-5 border-y border-[#e1e3d8] bg-[#eeefe6]">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        {/* Section header */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2.5">
            <div className="fe-home-icon fe-home-icon--sage">
              <Star className="w-5 h-5" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-gray-900">나만의 바로가기</h2>
              <p className="text-xs text-gray-500">자주 쓰는 도구를 여기에 모아두세요</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {resolvedTools.length > 0 && (
              <button
                onClick={() => setIsEditing((p) => !p)}
                className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all duration-200 ${
                  isEditing
                    ? "bg-red-50 text-red-600 border border-red-200 hover:bg-red-100"
                    : "bg-gray-100 text-gray-600 hover:bg-gray-200 border border-transparent"
                }`}
              >
                {isEditing ? "완료" : "편집"}
              </button>
            )}
            <button
              onClick={() => setPickerOpen(true)}
              disabled={!isLoaded}
              className="fe-primary-action inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full text-xs font-semibold bg-gradient-to-r from-blue-600 to-purple-600 text-white hover:shadow-lg hover:shadow-blue-200/50 hover:scale-105 active:scale-95 transition-all duration-200 disabled:opacity-50"
            >
              <Plus className="w-3.5 h-3.5" />
              추가
            </button>
          </div>
        </div>

        {/* Quick access grid */}
        <div className="grid grid-cols-4 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-8 gap-3">
          {/* Filled slots */}
          {resolvedTools.map((tool) => {
            const IconComp = tool.icon;
            const animKey = `${tool.service}:${tool.id}`;
            const isRemoving = removeAnimId === animKey;

            const cardInner = (
              <div
                data-workspace={tool.service}
                className={`fe-favorite-card relative flex flex-col items-center gap-2 p-3 sm:p-4 rounded-2xl border transition-all duration-300 group/card
                  ${isRemoving ? "scale-75 opacity-0" : "scale-100 opacity-100"}
                  ${isEditing
                    ? "bg-white border-red-100 shadow-sm animate-wiggle"
                    : "bg-white/80 backdrop-blur-sm border-gray-100 shadow-md hover:shadow-xl hover:-translate-y-1 hover:border-gray-200 cursor-pointer"
                  }`}
              >
                {/* Remove button in edit mode */}
                {isEditing && (
                  <button
                    aria-label={`${tool.name} 바로가기 삭제`}
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      removeFavorite(tool.service, tool.id);
                    }}
                    className="absolute -top-2 -right-2 z-10 w-6 h-6 rounded-full bg-red-500 text-white flex items-center justify-center shadow-lg hover:bg-red-600 hover:scale-110 active:scale-90 transition-all"
                  >
                    <X className="w-3.5 h-3.5" />
                  </button>
                )}

                <div className={`fe-home-icon fe-home-icon--${tool.service === "workmate" ? "lavender" : "sage"}`}>
                  <IconComp className="w-5 h-5 sm:w-6 sm:h-6" />
                </div>

                {/* Name */}
                <span className="text-[11px] sm:text-xs font-semibold text-gray-700 text-center leading-tight line-clamp-2 min-h-[28px] flex items-center">
                  {tool.shortName || tool.name}
                </span>
              </div>
            );

            if (isEditing) {
              return <div key={animKey}>{cardInner}</div>;
            }

            if (tool.href) {
              return (
                <a key={animKey} href={tool.href} target="_blank" rel="noopener noreferrer">
                  {cardInner}
                </a>
              );
            }

            return (
              <Link key={animKey} to={tool.to!}>
                {cardInner}
              </Link>
            );
          })}

          {/* Empty slots */}
          {Array.from({ length: emptySlots }).map((_, i) => (
            <button
              key={`empty-${i}`}
              onClick={() => setPickerOpen(true)}
              disabled={!isLoaded}
              className="fe-favorite-empty flex flex-col items-center justify-center gap-2 p-3 sm:p-4 rounded-2xl border-2 border-dashed border-gray-200 hover:border-blue-300 hover:bg-blue-50/50 transition-all duration-300 group/empty min-h-[100px] sm:min-h-[110px] disabled:opacity-50"
            >
              <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl bg-gray-100 group-hover/empty:bg-blue-100 flex items-center justify-center transition-colors duration-300">
                <Plus className="w-5 h-5 sm:w-6 sm:h-6 text-gray-400 group-hover/empty:text-blue-500 transition-colors duration-300" />
              </div>
              <span className="text-[10px] sm:text-[11px] font-medium text-gray-400 group-hover/empty:text-blue-500 transition-colors duration-300">
                도구 추가
              </span>
            </button>
          ))}
        </div>

        {/* Hint for empty state */}
        {isLoaded && resolvedTools.length === 0 && (
          <div className="mt-4 text-center">
            <p className="text-sm text-gray-500">
              <span className="font-medium text-gray-700">위의 빈 슬롯</span>을 눌러 자주 쓰는 도구를 추가해보세요!
            </p>
            <p className="text-xs text-gray-400 mt-1">
              워크메이트와 클래스메이트의 모든 도구를 바로가기로 등록할 수 있어요
            </p>
          </div>
        )}
      </div>

      {/* ─── Picker Modal ─── */}
      {pickerOpen && (
        <div className="fixed inset-0 z-[9999] flex items-end sm:items-center justify-center">
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-black/40 backdrop-blur-sm animate-fade-in"
            onClick={() => setPickerOpen(false)}
          />

          {/* Modal */}
          <div ref={pickerRef} role="dialog" aria-modal="true" aria-labelledby="favorites-picker-title" className="fe-favorites-picker relative w-full max-w-2xl max-h-[85vh] bg-white rounded-t-3xl sm:rounded-3xl shadow-2xl overflow-hidden animate-slide-up flex flex-col">
            {/* Header */}
            <div className="sticky top-0 z-10 bg-white/95 backdrop-blur-sm border-b border-gray-100 px-6 py-4">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2.5">
                  <div className="fe-home-icon fe-home-icon--sage">
                    <Sparkles className="w-5 h-5" />
                  </div>
                  <div>
                    <h3 id="favorites-picker-title" className="text-lg font-bold text-gray-900">바로가기 추가</h3>
                    <p className="text-xs text-gray-500">
                      {favorites.length}/{MAX_SLOTS}개 선택됨
                    </p>
                  </div>
                </div>
                <button
                  aria-label="바로가기 선택 닫기"
                  onClick={() => setPickerOpen(false)}
                  className="w-8 h-8 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center transition-colors"
                >
                  <X className="w-4 h-4 text-gray-600" />
                </button>
              </div>

              {/* Tabs */}
              <div className="flex gap-1 p-1 bg-gray-100 rounded-xl">
                <button
                  onClick={() => setPickerTab("workmate")}
                  className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-lg text-sm font-semibold transition-all duration-200 ${
                    pickerTab === "workmate"
                      ? "bg-white text-[#655878] shadow-sm"
                      : "text-gray-500 hover:text-gray-700"
                  }`}
                >
                  <Briefcase className="w-4 h-4" />
                  워크메이트
                  <span className="text-xs opacity-60">({WORKMATE_TOOLS.length})</span>
                </button>
                <button
                  onClick={() => setPickerTab("classmate")}
                  className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-lg text-sm font-semibold transition-all duration-200 ${
                    pickerTab === "classmate"
                      ? "bg-white text-[#536541] shadow-sm"
                      : "text-gray-500 hover:text-gray-700"
                  }`}
                >
                  <GraduationCap className="w-4 h-4" />
                  클래스메이트
                  <span className="text-xs opacity-60">({CLASSMATE_TOOLS.length})</span>
                </button>
              </div>
            </div>

            {/* Tool list */}
            <div className="flex-1 overflow-y-auto px-6 py-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                {(pickerTab === "workmate" ? WORKMATE_TOOLS : CLASSMATE_TOOLS).map((tool) => {
                  const selected = isFavorited(tool.service, tool.id);
                  const full = favorites.length >= MAX_SLOTS && !selected;
                  const IconComp = tool.icon;

                  return (
                    <button
                      key={`${tool.service}:${tool.id}`}
                      onClick={() => toggleFavorite(tool.service, tool.id)}
                      aria-pressed={selected}
                      disabled={full}
                      className={`flex items-center gap-3 px-4 py-3 rounded-xl border-2 text-left transition-all duration-200 ${
                        selected
                          ? "border-[#adb995] bg-[#eef2e6] shadow-sm"
                          : full
                          ? "border-gray-100 bg-gray-50 opacity-50 cursor-not-allowed"
                          : "border-gray-100 bg-white hover:border-gray-200 hover:bg-gray-50 hover:shadow-sm"
                      }`}
                    >
                      <div
                        className={`fe-home-icon fe-home-icon--${tool.service === "workmate" ? "lavender" : "sage"}`}>
                        <IconComp className="w-5 h-5" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold text-gray-900 truncate">{tool.name}</p>
                      </div>
                      <div
                        className={`w-6 h-6 rounded-full border-2 flex items-center justify-center flex-shrink-0 transition-all duration-200 ${
                          selected
                            ? "border-[#536541] bg-[#536541]"
                            : "border-gray-300 bg-white"
                        }`}
                      >
                        {selected && (
                          <svg className="w-3.5 h-3.5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                          </svg>
                        )}
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Footer */}
            <div className="sticky bottom-0 bg-white/95 backdrop-blur-sm border-t border-gray-100 px-6 py-4">
              <button
                onClick={() => setPickerOpen(false)}
                className="fe-primary-action w-full py-3 rounded-xl bg-gradient-to-r from-blue-600 to-purple-600 text-white font-semibold text-sm hover:shadow-lg hover:shadow-blue-200/50 active:scale-[0.98] transition-all duration-200"
              >
                완료
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ─── Inline styles for animations ─── */}
      <style>{`
        @keyframes fade-in {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes slide-up {
          from { opacity: 0; transform: translateY(40px) scale(0.97); }
          to { opacity: 1; transform: translateY(0) scale(1); }
        }
        @keyframes wiggle {
          0%, 100% { transform: rotate(0deg); }
          25% { transform: rotate(-1.5deg); }
          75% { transform: rotate(1.5deg); }
        }
        .animate-fade-in {
          animation: fade-in 0.2s ease-out both;
        }
        .animate-slide-up {
          animation: slide-up 0.35s cubic-bezier(0.16, 1, 0.3, 1) both;
        }
        .animate-wiggle {
          animation: wiggle 0.4s ease-in-out infinite;
        }
      `}</style>
    </section>
  );
}

export default QuickFavorites;
